import * as React from 'react';
import { Check } from 'lucide-react';
import { StatusChip, type StatusChipTone } from '@/components/ui/status-chip';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils/cn';
import { ACTION_KIND_LABEL, type ActionKind, type OverviewAction } from './overview-plan';
import { OVERVIEW_LINK, OverviewGo, type OverviewCallbacks } from './overview-go';

/**
 * "Do next" on the engagement Overview (5 Nov 2026): the work waiting on
 * the team, in the order `overviewActions` gives (blocked, to do, waiting,
 * check), each row with one button to the screen where it is done. Three
 * rows show; the rest sit behind one link, so the card stays short. AI
 * proposals carry the AI surface and mark, as everywhere else.
 */

const SHOWN = 3;

const KIND_TONE: Record<ActionKind, StatusChipTone> = {
  blocked: 'critical',
  todo: 'amber',
  waiting: 'neutral',
  check: 'unset',
  wrap: 'neutral',
};

export function DoNextCard({
  engagementId,
  actions,
  loading,
  callbacks,
}: {
  engagementId: string;
  actions: readonly OverviewAction[];
  /** A read the list depends on is still in flight. */
  loading: boolean;
  callbacks: OverviewCallbacks;
}) {
  const [all, setAll] = React.useState(false);
  const pending = actions.filter((a) => a.kind !== 'wrap').length;
  const shown = all ? actions : actions.slice(0, SHOWN);
  const hidden = actions.length - SHOWN;

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="do-next-title"
      data-testid="do-next"
    >
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 id="do-next-title" className="t-h3">
          Do next
        </h2>
        {!loading ? (
          <span className="t-meta tabular-nums">
            {pending > 0 ? `${pending.toLocaleString('en-US')} pending` : 'Nothing pending'}
          </span>
        ) : null}
      </div>

      {loading ? (
        <p className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Working out what is next…
        </p>
      ) : (
        <>
          {pending === 0 ? (
            <p className="flex items-center gap-2.5 px-2.5 py-2 text-body-sm font-semibold text-ink-900">
              <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-posture-healthy text-paper-0">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              Nothing is waiting on you.
            </p>
          ) : null}
          {shown.length > 0 ? (
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {shown.map((a, i) => (
                <li
                  key={a.key}
                  className={cn(
                    'grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 rounded-card px-2.5 py-2 sm:grid-cols-[76px_1fr_auto]',
                    a.ai && 'bg-ai-surface',
                  )}
                >
                  <span className="col-span-2 sm:col-span-1">
                    <StatusChip tone={KIND_TONE[a.kind]} size="sm">
                      {ACTION_KIND_LABEL[a.kind]}
                    </StatusChip>
                  </span>
                  <span className="min-w-0">
                    {a.ai ? (
                      <StatusChip tone="ai" size="sm" className="mr-2 align-[1px]">
                        AI
                      </StatusChip>
                    ) : null}
                    <span className="text-body-sm font-semibold tabular-nums text-ink-900">
                      {a.title}
                    </span>
                    {a.note ? (
                      <span className="ml-2 text-meta text-ink-500 max-sm:ml-0 max-sm:block">
                        {a.note}
                      </span>
                    ) : null}
                  </span>
                  <OverviewGo
                    engagementId={engagementId}
                    go={a.go}
                    callbacks={callbacks}
                    button={i === 0 && pending > 0 ? 'primary' : 'secondary'}
                  >
                    {a.label}
                  </OverviewGo>
                </li>
              ))}
            </ul>
          ) : null}
          {hidden > 0 ? (
            <button
              type="button"
              onClick={() => setAll((v) => !v)}
              aria-expanded={all}
              className={cn(OVERVIEW_LINK, 'ml-2.5 mt-2')}
            >
              {all ? 'Show fewer' : `${hidden.toLocaleString('en-US')} more`}
            </button>
          ) : null}
        </>
      )}
    </section>
  );
}
